import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Progress } from 'reactstrap';
import config from 'config';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { removeActiveJob, upsertActiveJob } from 'slices/sinhBoSoQr/reducer';
import { QrGenerationJob, toJobStatus } from 'pages/SinhBoSoQr/types';
import { useJobProgress } from 'Components/Hooks/useJobProgress';
import { usePrintPackageJobProgress } from 'Components/Hooks/usePrintPackageJobProgress';
import { useQrImageJobProgress } from 'Components/Hooks/useQrImageJobProgress';
import ProgressDialog from 'pages/SinhBoSoQr/modals/ProgressDialog';
import JobErrorLogList from 'Components/Common/JobErrorLogList';
import ReasonPromptModal from 'Components/Common/ReasonPromptModal';
import {
  getStoredActivePrintExportJobs,
  setStoredActivePrintExportJob,
  clearStoredActivePrintExportJob,
  getStoredActiveQrImageExportJobs,
  setStoredActiveQrImageExportJob,
  clearStoredActiveQrImageExportJob,
  JOB_PANEL_TOGGLE_REQUEST_EVENT,
  JOB_PANEL_STATE_EVENT,
} from 'helpers/activeExportJobsStorage';
import { useCancelQrGenerationJob, QR_GENERATION_JOB_TERMINAL_EVENT } from 'hooks/useCancelQrGenerationJob';
import {
  cancelPrintPackageExportJobApi,
  cancelQrImageExportJobApi,
  downloadPrintPackageExportJob,
  downloadQrImageExportJob,
  getActiveTicketJobsApi,
  getJobProgressApi,
  getPrintPackageExportJob,
  getPrintPackageJobErrorLogsApi,
  getQrImageExportJob,
  getQrImageJobErrorLogsApi,
  sendToPrintIssuancePlan,
} from 'helpers/fakebackend_helper';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('layout_global_job_progress');

type PrintPackageExportJob = {
  jobId: string;
  issuancePlanId: number;
  lotteryPeriodId?: number;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'stalled' | string;
  totalRows: number;
  processedRows: number;
  progressPercent: number;
  fileName?: string | null;
  errorMessage?: string | null;
};

// Job xuat file anh QR (.png, gom theo Symbol, nen zip) — chay song song, doc lap voi
// PrintPackageExportJob (Gui in) o tren. Cung shape DTO (JobId/IssuancePlanId/Status/...).
type QrImageExportJob = PrintPackageExportJob;

// Key luu localStorage theo tung jobId (cho phep nhieu ky export song song deu duoc khoi phuc lai
// sau F5) nam trong helpers/activeExportJobsStorage.ts — dung chung voi man Lap ke hoach de hien
// thi progress ngay trong cot trang thai.
const PRINT_EXPORT_JOB_STARTED_EVENT = 'lms_print_export_job_started';
// Bao cho trang Ke hoach phat hanh biet job export vua chuyen sang trang thai loi (failed/stalled)
// de tu load lai danh sach — tranh nut "Gửi in" bi ket o trang thai disabled (canSendToPrint cu,
// tinh tu truoc khi job that bai) neu nguoi dung dang dung san o trang do luc job that bai.
const PRINT_EXPORT_JOB_TERMINAL_EVENT = 'lms_print_export_job_terminal';
// Tuong tu 2 hang so tren, ap dung cho luong xuat anh QR song song.
const QR_IMAGE_EXPORT_JOB_STARTED_EVENT = 'lms_qr_image_export_job_started';
const QR_IMAGE_EXPORT_JOB_TERMINAL_EVENT = 'lms_qr_image_export_job_terminal';
// QR_GENERATION_JOB_TERMINAL_EVENT gio import tu hooks/useCancelQrGenerationJob.ts (dung chung voi
// nut Huy trong cot Thao tac cua man Sinh Bo So & QR) thay vi tu khai bao rieng o day.
// Khoang cach giua cac nut tron (thu gon) va giua cac panel (mo rong) khi xep chong theo chieu doc.
const COLLAPSED_SLOT_HEIGHT = 56;
const EXPANDED_SLOT_HEIGHT = 268;
const STACK_TOP_START = 88;

// Ca 2 effect khoi phuc job export (Gui in va Xuat anh QR) o duoi deu can goi getActiveTicketJobsApi
// (jobs/active) rieng, gay goi trung API nay 2 lan tren cung 1 lan mount trang. Gom chung 1 promise
// dang chay (module scope) de 2 effect dung lai cung 1 ket qua thay vi ban ra 2 request HTTP.
let activeTicketJobsPromise: Promise<any> | null = null;
const fetchActiveTicketJobsOnce = () => {
  if (!activeTicketJobsPromise) {
    activeTicketJobsPromise = getActiveTicketJobsApi().finally(() => {
      activeTicketJobsPromise = null;
    });
  }
  return activeTicketJobsPromise;
};

const formatNumber = (value?: number | null) => Number(value || 0).toLocaleString('vi-VN');

const normalizeExportJob = (job: any): PrintPackageExportJob | null => {
  if (!job) return null;

  const jobId = job.jobId ?? job.JobId;
  if (!jobId) return null;

  return {
    jobId,
    issuancePlanId: Number(job.issuancePlanId ?? job.IssuancePlanId ?? 0),
    lotteryPeriodId: job.lotteryPeriodId ?? job.LotteryPeriodId,
    status: toJobStatus(job.status ?? job.Status),
    totalRows: Number(job.totalRows ?? job.TotalRows ?? 0),
    processedRows: Number(job.processedRows ?? job.ProcessedRows ?? 0),
    progressPercent: Number(job.progressPercent ?? job.ProgressPercent ?? 0),
    fileName: job.fileName ?? job.FileName ?? null,
    errorMessage: job.errorMessage ?? job.ErrorMessage ?? null,
  };
};

// Backend gio la 1 gate proxy qua ticket-service (khong con tra ve URL cong khai tren MinIO nua —
// xem ticket-service/AGENTS.md muc download-file), Url tra ve la duong dan tuong doi qua gateway
// (VD: /ticket-api/IssuancePlan/.../download-file?dtoken=...) kem theo dtoken ngan han dung 1 lan.
// Trinh duyet van tai truc tiep bang the <a download> (khong qua fetch/blob) de OS tu stream file
// lon xuong dia thay vi giu ca file trong bo nho JS.
const resolveDownloadUrl = (url: string) => (/^https?:\/\//i.test(url) ? url : `${config.api.API_URL}${url}`);

const triggerBrowserDownload = (url: string, fileName: string) => {
  const link = document.createElement('a');
  link.href = resolveDownloadUrl(url);
  link.setAttribute('download', fileName);
  link.setAttribute('rel', 'noopener');
  document.body.appendChild(link);
  link.click();
  link.remove();
};

// 1 instance / ky dang sinh QR — tu polling tien trinh cua rieng job nay va hien popup rieng,
// khong con dung chung 1 slot "activeJob" toan cuc nen nhieu ky sinh song song deu duoc theo doi.
const QrJobTracker: React.FC<{
  job: QrGenerationJob;
  periodName?: string | null;
  topOffset: number;
  collapsed: boolean;
  onToggleCollapse: (collapsed: boolean) => void;
  onVerify: (jobId: string, lotteryPeriodId: number) => void;
}> = ({ job, periodName, topOffset, collapsed, onToggleCollapse, onVerify }) => {
  const dispatch = useDispatch();
  const cancelQrGenerationJob = useCancelQrGenerationJob();
  const status = String(job.status || '').toLowerCase();
  const shouldListen = status !== 'completed' && status !== 'failed' && status !== 'stalled' && status !== 'cancelled';

  useJobProgress(
    shouldListen ? job.jobId : null,
    job.lotteryPeriodId,
    (message) => {
      const messageStatus = String(message.status || '').toLowerCase();
      if (messageStatus === 'completed') {
        onVerify(message.jobId, job.lotteryPeriodId);
      }
    },
  );

  const handleClose = useCallback(() => {
    if (status === 'completed') {
      onVerify(job.jobId, job.lotteryPeriodId);
    } else {
      dispatch(removeActiveJob(job.jobId));
      window.dispatchEvent(new CustomEvent(QR_GENERATION_JOB_TERMINAL_EVENT));
    }
  }, [dispatch, job.jobId, job.lotteryPeriodId, onVerify, status]);

  // README.md muc 13 — huy giua luc dang sinh QR ("Huy tao ve", bat buoc nhap ly do, xem
  // ReasonPromptModal trong ProgressDialog.tsx) gio dung CHUNG logic voi nut Huy trong cot Thao tac
  // cua man Sinh Bo So & QR (useCancelQrGenerationJob) — TRUOC DAY o day KHONG dieu huong sau khi
  // huy (chi dong popup, o lai trang hien tai), khac voi cot Thao tac (co dieu huong sang
  // issuance-plans), khien man Ke hoach phat hanh "dung yen" voi du lieu cu neu nguoi dung tu chuyen
  // trang sau khi huy tu day thay vi duoc dieu huong thang toi. Gio ca 2 noi cung dieu huong sang
  // issuance-plans ngay sau khi huy thanh cong, dam bao danh sach luon duoc tai lai dung trang thai.
  const handleCancel = useCallback(async (reason: string) => {
    try {
      await cancelQrGenerationJob(job.jobId, reason, job.lotteryPeriodId);
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_huy_job_sinh_bo_message'));
    }
  }, [cancelQrGenerationJob, job.jobId, job.lotteryPeriodId]);

  return (
    <ProgressDialog
      isOpen
      job={job}
      periodName={periodName}
      topOffset={topOffset}
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      onCancel={handleCancel}
      onClose={handleClose}
    />
  );
};

// 1 instance / job export gói in dang chay — tu polling, tu tai file khi hoan tat, hien popup rieng.
const ExportJobCard: React.FC<{
  job: PrintPackageExportJob;
  topOffset: number;
  collapsed: boolean;
  onToggleCollapse: (collapsed: boolean) => void;
  onUpdate: (jobId: string, patch: Partial<PrintPackageExportJob>) => void;
  onRemove: (jobId: string) => void;
}> = ({ job, topOffset, collapsed, onToggleCollapse, onUpdate, onRemove }) => {
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const [sendingToPrint, setSendingToPrint] = useState(false);
  const [sentToPrintDone, setSentToPrintDone] = useState(false);
  const [sendToPrintFailed, setSendToPrintFailed] = useState(false);
  const sentToPrintRef = useRef(false);
  const autoDownloadedRef = useRef(false);
  const [cancelling, setCancelling] = useState(false);
  const [reasonModalOpen, setReasonModalOpen] = useState(false);

  const status = String(job.status || '').toLowerCase();
  const isTerminal = status === 'completed' || status === 'failed' || status === 'stalled' || status === 'cancelled';
  const canCancel = status === 'pending' || status === 'processing';
  const progressPercent = Math.max(0, Math.min(100, Number(job.progressPercent || 0)));

  // Huy xuat vé — bat buoc nhap ly do (ReasonPromptModal). An het popup ngay sau khi huy thanh cong
  // (onRemove) thay vi giu lai card o trang thai "cancelled" cho nguoi dung tu dong — nguoi dung
  // van co the bam "Gửi in" lai binh thuong sau do (khong bi chan boi lan huy nay).
  const handleCancel = useCallback(async (reason: string) => {
    if (cancelling) return;
    setCancelling(true);
    try {
      await cancelPrintPackageExportJobApi(job.jobId, reason);
      clearStoredActivePrintExportJob(job.jobId);
      window.dispatchEvent(new CustomEvent(PRINT_EXPORT_JOB_TERMINAL_EVENT));
      toast.success(st('da_huy_xuat_ve_message'));
      setReasonModalOpen(false);
      onRemove(job.jobId);
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_huy_job_export_goi_message'));
    } finally {
      setCancelling(false);
    }
  }, [cancelling, job.jobId, onRemove]);

  usePrintPackageJobProgress(!isTerminal ? job.jobId : null, (message) => {
    onUpdate(job.jobId, message);
    const messageStatus = String(message.status || '').toLowerCase();
    if (messageStatus === 'failed' || messageStatus === 'stalled') {
      clearStoredActivePrintExportJob(job.jobId);
      window.dispatchEvent(new CustomEvent(PRINT_EXPORT_JOB_TERMINAL_EVENT));
    }
  });

  // Tach rieng thanh ham co the goi lai thu cong (nut "Thử lại gửi in") — that bai o buoc nay
  // KHONG lam mat file export da co tren MinIO, chi can goi lai chinh API nay, khong phai export
  // lai tu dau. Trang thai ke hoach (plan.Status) giu nguyen o GenQr cho toi khi goi nay thanh cong
  // (xem SendToPrintIssuancePlanCommand — chay trong transaction, that bai thi rollback het).
  const handleSendToPrintNow = useCallback(async () => {
    if (sendingToPrint || !job.issuancePlanId) return;
    setSendingToPrint(true);
    setSendToPrintFailed(false);
    try {
      await sendToPrintIssuancePlan(job.issuancePlanId);
      clearStoredActivePrintExportJob(job.jobId);
      // Bao cho cot trang thai trong bang Ke hoach phat hanh (PlanStatusColumn.tsx) biet toan bo
      // qua trinh (xuat CSV + gui nha in) da xong that su — cot do dang giu progress bar rieng cho
      // buoc "Dang gui nha in..." sau khi job export da "completed", chi tat progress qua event nay
      // (khong tat ngay luc job export completed, vi luc do buoc gui nha in van dang chay).
      window.dispatchEvent(new CustomEvent(PRINT_EXPORT_JOB_TERMINAL_EVENT));
      setSentToPrintDone(true);
      toast.success(st('goi_in_da_export_xong_va_message'));
      navigate('/issuance-plans', { state: { refreshAt: Date.now(), printJobId: job.jobId } });
    } catch (error: any) {
      setSendToPrintFailed(true);
      toast.error(error?.message || st('khong_the_cap_nhat_trang_thai_message'));
    } finally {
      setSendingToPrint(false);
    }
  }, [sendingToPrint, job.issuancePlanId, job.jobId, navigate]);

  useEffect(() => {
    if (status !== 'completed' || sentToPrintRef.current || !job.issuancePlanId) return;
    sentToPrintRef.current = true;
    handleSendToPrintNow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const handleDownload = useCallback(async () => {
    if (downloading) return;
    setDownloading(true);
    setDownloadFailed(false);
    try {
      const response: any = await downloadPrintPackageExportJob(job.jobId);
      const payload = response?.data || response;
      const downloadUrl = payload?.url;
      if (!downloadUrl) throw new Error(st('khong_nhan_duoc_duong_dan_tai_message'));

      triggerBrowserDownload(downloadUrl, payload?.fileName || job.fileName || `print-package-${job.jobId}.zip`);
      setDownloaded(true);
      toast.success(st('tai_goi_in_thanh_cong_message'));
    } catch (error: any) {
      setDownloadFailed(true);
      toast.error(error?.message || st('khong_the_tai_goi_in_message'));
    } finally {
      setDownloading(false);
    }
  }, [downloading, job.jobId, job.fileName]);

  useEffect(() => {
    if (status !== 'completed' || autoDownloadedRef.current) return;
    autoDownloadedRef.current = true;
    handleDownload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Tu an popup sau 3s (giong hanh vi sinh QR) CHI KHI ca tai file lan gui in deu da thanh cong —
  // neu buoc nao that bai thi giu nguyen popup de nguoi dung con thay nut "Thu lai" tuong ung,
  // khong bi mat ngang trong luc chua xu ly xong.
  useEffect(() => {
    if (!downloaded || !sentToPrintDone) return;
    const timer = setTimeout(() => {
      onRemove(job.jobId);
    }, 3000);
    return () => clearTimeout(timer);
  }, [downloaded, sentToPrintDone, job.jobId, onRemove]);

  const iconClass =
    status === 'completed'
      ? 'ri-check-double-line text-success'
      : status === 'failed'
        ? 'ri-close-circle-line text-danger'
        : status === 'stalled'
          ? 'ri-alarm-warning-line text-warning'
          : status === 'cancelled'
            ? 'ri-forbid-line text-secondary'
            : 'ri-loader-4-line text-primary';

  const handleClose = () => {
    if (isTerminal) {
      onRemove(job.jobId);
    } else {
      onToggleCollapse(true);
    }
  };

  if (collapsed) {
    return (
      <div className="position-fixed" style={{ top: topOffset, right: 24, zIndex: 1060 }}>
        <button
          type="button"
          className="btn btn-light border shadow-lg d-flex align-items-center gap-2 rounded-pill py-2 px-3"
          title={st('phong_to_tien_trinh_export_goi_title')}
          onClick={() => onToggleCollapse(false)}
        >
          {downloading ? (
            <span className="spinner-border spinner-border-sm text-info" role="status" aria-hidden="true"></span>
          ) : (
            <i className={`${iconClass} fs-5`}></i>
          )}
          <span className="fw-semibold small">
            {downloading ? st('dang_tai_text') : `${progressPercent.toFixed(0)}%`}
          </span>
        </button>
      </div>
    );
  }

  return (
    <div
      className="position-fixed"
      style={{ top: topOffset, right: 24, zIndex: 1060, width: 'min(420px, calc(100vw - 32px))' }}
    >
      <Alert color="light" className="border shadow-lg mb-0" fade={false}>
        <div className="d-flex align-items-start gap-3">
          {downloading ? (
            <span className="spinner-border spinner-border-sm text-info mt-1" role="status" aria-hidden="true"></span>
          ) : (
            <i className={`${iconClass} fs-3`}></i>
          )}
          <div className="flex-grow-1 min-w-0">
            <div className="d-flex align-items-start justify-content-between gap-2">
              <div>
                <div className="fw-semibold">{st('tien_trinh_export_goi_in_text')}</div>
                <div className="text-muted small text-truncate">{st('job_text')} {job.jobId}</div>
              </div>
              <div className="d-flex align-items-center gap-1">
                {canCancel && (
                  <Button
                    color="danger"
                    outline
                    size="sm"
                    className="py-0 px-2"
                    disabled={cancelling}
                    onClick={() => setReasonModalOpen(true)}
                  >
                    {cancelling && (
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    )}
                    {st('huy_button')}
                  </Button>
                )}
                <button
                  type="button"
                  className="btn btn-sm btn-light p-1 lh-1"
                  aria-label={st('thu_nho_label')}
                  title={st('thu_nho_label')}
                  onClick={() => onToggleCollapse(true)}
                >
                  <i className="ri-subtract-line"></i>
                </button>
                {isTerminal && (
                  <Button close type="button" aria-label={st('dong_label')} onClick={handleClose} />
                )}
              </div>
            </div>

            <div className="mt-3">
              <div className="d-flex justify-content-between small mb-1">
                <span className="fw-semibold">{job.status}</span>
                <span className="fw-semibold">{progressPercent.toFixed(2)}%</span>
              </div>
              <Progress
                value={progressPercent}
                color={
                  status === 'failed'
                    ? 'danger'
                    : status === 'stalled'
                      ? 'warning'
                      : status === 'cancelled'
                        ? 'secondary'
                        : status === 'completed'
                          ? 'success'
                          : 'primary'
                }
                style={{ height: '10px' }}
                animated={!isTerminal}
              />
            </div>

            <div className="d-flex justify-content-between text-muted small mt-2">
              <span>{st('da_xu_ly_text')}</span>
              <span className="fw-semibold">
                {formatNumber(job.processedRows)} / {formatNumber(job.totalRows)}
              </span>
            </div>

            {status === 'cancelled' && (
              <div className="small mt-2 text-muted">{st('da_huy_theo_yeu_cau_nguoi_text')}</div>
            )}

            {status === 'completed' && (
              <div className="d-flex flex-column gap-2 mt-3">
                <div className="d-flex align-items-center gap-2">
                  {downloaded ? (
                    <span className="text-success small">
                      <i className="ri-checkbox-circle-fill me-1"></i>
                      {st('tai_goi_in_thanh_cong_text')}
                    </span>
                  ) : downloadFailed ? (
                    <>
                      <Button color="success" size="sm" onClick={handleDownload} disabled={downloading}>
                        {downloading && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                        {st('thu_tai_lai_button')}
                      </Button>
                      <span className="text-danger small">{st('tai_goi_in_that_bai_vui_text')}</span>
                    </>
                  ) : (
                    <>
                      <span className="spinner-border spinner-border-sm text-success" role="status" aria-hidden="true"></span>
                      <span className="text-success small">{st('goi_in_da_san_sang_dang_text')}</span>
                    </>
                  )}
                </div>

                <div className="d-flex align-items-center gap-2">
                  {sentToPrintDone ? (
                    <span className="text-success small">
                      <i className="ri-checkbox-circle-fill me-1"></i>
                      {st('da_chuyen_trang_thai_gui_in_text')}
                    </span>
                  ) : sendToPrintFailed ? (
                    <>
                      <Button color="warning" size="sm" onClick={handleSendToPrintNow} disabled={sendingToPrint}>
                        {sendingToPrint && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                        {st('thu_lai_gui_in_button')}
                      </Button>
                      <span className="text-danger small">{st('cap_nhat_trang_thai_gui_in_text')}</span>
                    </>
                  ) : (
                    <>
                      <span className="spinner-border spinner-border-sm text-success" role="status" aria-hidden="true"></span>
                      <span className="text-success small">{st('dang_cap_nhat_trang_thai_gui_text')}</span>
                    </>
                  )}
                </div>
              </div>
            )}

            {(status === 'failed' || status === 'stalled') && (
              <>
                <div className={`small mt-2 text-break ${status === 'stalled' ? 'text-warning' : 'text-danger'}`}>
                  {job.errorMessage || st('export_goi_in_that_bai_text')}
                </div>
                <div className="text-muted small mt-2 fst-italic">
                  {st('bam_gui_in_de_tao_lai_text')}
                </div>
                <JobErrorLogList fetchLogs={() => getPrintPackageJobErrorLogsApi(job.issuancePlanId)} />
              </>
            )}
          </div>
        </div>
      </Alert>

      <ReasonPromptModal
        isOpen={reasonModalOpen}
        title={st('huy_xuat_ve_title')}
        label={st('ly_do_huy_xuat_ve_label')}
        confirmLabel={st('huy_xuat_ve_title')}
        submitting={cancelling}
        onCancel={() => setReasonModalOpen(false)}
        onSubmit={handleCancel}
      />
    </div>
  );
};

// 1 instance / job xuat anh QR dang chay — tu polling, tu tai file khi hoan tat, hien popup rieng.
// Don gian hon ExportJobCard: khong co buoc "gui in" tiep theo, chi tai file roi xong.
const QrImageExportJobCard: React.FC<{
  job: QrImageExportJob;
  topOffset: number;
  collapsed: boolean;
  onToggleCollapse: (collapsed: boolean) => void;
  onUpdate: (jobId: string, patch: Partial<QrImageExportJob>) => void;
  onRemove: (jobId: string) => void;
}> = ({ job, topOffset, collapsed, onToggleCollapse, onUpdate, onRemove }) => {
  const [downloading, setDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const autoDownloadedRef = useRef(false);
  const [cancelling, setCancelling] = useState(false);
  const [reasonModalOpen, setReasonModalOpen] = useState(false);

  const status = String(job.status || '').toLowerCase();
  const isTerminal = status === 'completed' || status === 'failed' || status === 'stalled' || status === 'cancelled';
  const canCancel = status === 'pending' || status === 'processing';
  const progressPercent = Math.max(0, Math.min(100, Number(job.progressPercent || 0)));

  // Huy xuat anh QR — bat buoc nhap ly do, an het popup ngay sau khi huy thanh cong (xem
  // ExportJobCard.handleCancel cho logic tuong tu, cung an popup ngay thay vi cho tu dong).
  const handleCancel = useCallback(async (reason: string) => {
    if (cancelling) return;
    setCancelling(true);
    try {
      await cancelQrImageExportJobApi(job.jobId, reason);
      clearStoredActiveQrImageExportJob(job.jobId);
      window.dispatchEvent(new CustomEvent(QR_IMAGE_EXPORT_JOB_TERMINAL_EVENT));
      toast.success(st('da_huy_xuat_anh_qr_message'));
      setReasonModalOpen(false);
      onRemove(job.jobId);
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_huy_job_xuat_anh_message'));
    } finally {
      setCancelling(false);
    }
  }, [cancelling, job.jobId, onRemove]);

  useQrImageJobProgress(!isTerminal ? job.jobId : null, (message) => {
    onUpdate(job.jobId, message);
    const messageStatus = String(message.status || '').toLowerCase();
    if (messageStatus === 'failed' || messageStatus === 'stalled') {
      clearStoredActiveQrImageExportJob(job.jobId);
      window.dispatchEvent(new CustomEvent(QR_IMAGE_EXPORT_JOB_TERMINAL_EVENT));
    }
  });

  const handleDownload = useCallback(async () => {
    if (downloading) return;
    setDownloading(true);
    setDownloadFailed(false);
    try {
      const response: any = await downloadQrImageExportJob(job.jobId);
      const payload = response?.data || response;
      const downloadUrl = payload?.url;
      if (!downloadUrl) throw new Error(st('khong_nhan_duoc_duong_dan_tai_message'));

      triggerBrowserDownload(downloadUrl, payload?.fileName || job.fileName || `qr-images-${job.jobId}.zip`);
      clearStoredActiveQrImageExportJob(job.jobId);
      setDownloaded(true);
      toast.success(st('tai_file_anh_qr_thanh_cong_message'));
    } catch (error: any) {
      setDownloadFailed(true);
      toast.error(error?.message || st('khong_the_tai_file_anh_qr_message'));
    } finally {
      setDownloading(false);
    }
  }, [downloading, job.jobId, job.fileName]);

  useEffect(() => {
    if (status !== 'completed' || autoDownloadedRef.current) return;
    autoDownloadedRef.current = true;
    handleDownload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Tu an popup sau 3s CHI KHI tai thanh cong — that bai thi giu popup de con thay nut "Thu tai lai".
  useEffect(() => {
    if (!downloaded) return;
    const timer = setTimeout(() => {
      onRemove(job.jobId);
    }, 3000);
    return () => clearTimeout(timer);
  }, [downloaded, job.jobId, onRemove]);

  const iconClass =
    status === 'completed'
      ? 'ri-check-double-line text-success'
      : status === 'failed'
        ? 'ri-close-circle-line text-danger'
        : status === 'stalled'
          ? 'ri-alarm-warning-line text-warning'
          : status === 'cancelled'
            ? 'ri-forbid-line text-secondary'
            : 'ri-loader-4-line text-primary';

  const handleClose = () => {
    if (isTerminal) {
      onRemove(job.jobId);
    } else {
      onToggleCollapse(true);
    }
  };

  if (collapsed) {
    return (
      <div className="position-fixed" style={{ top: topOffset, right: 24, zIndex: 1060 }}>
        <button
          type="button"
          className="btn btn-light border shadow-lg d-flex align-items-center gap-2 rounded-pill py-2 px-3"
          title={st('phong_to_tien_trinh_xuat_anh_title')}
          onClick={() => onToggleCollapse(false)}
        >
          {downloading ? (
            <span className="spinner-border spinner-border-sm text-info" role="status" aria-hidden="true"></span>
          ) : (
            <i className={`${iconClass} fs-5`}></i>
          )}
          <span className="fw-semibold small">
            {downloading ? st('dang_tai_text') : `${progressPercent.toFixed(0)}%`}
          </span>
        </button>
      </div>
    );
  }

  return (
    <div
      className="position-fixed"
      style={{ top: topOffset, right: 24, zIndex: 1060, width: 'min(420px, calc(100vw - 32px))' }}
    >
      <Alert color="light" className="border shadow-lg mb-0" fade={false}>
        <div className="d-flex align-items-start gap-3">
          {downloading ? (
            <span className="spinner-border spinner-border-sm text-info mt-1" role="status" aria-hidden="true"></span>
          ) : (
            <i className={`${iconClass} fs-3`}></i>
          )}
          <div className="flex-grow-1 min-w-0">
            <div className="d-flex align-items-start justify-content-between gap-2">
              <div>
                <div className="fw-semibold">{st('tien_trinh_xuat_anh_qr_text')}</div>
                <div className="text-muted small text-truncate">{st('job_text')} {job.jobId}</div>
              </div>
              <div className="d-flex align-items-center gap-1">
                {canCancel && (
                  <Button
                    color="danger"
                    outline
                    size="sm"
                    className="py-0 px-2"
                    disabled={cancelling}
                    onClick={() => setReasonModalOpen(true)}
                  >
                    {cancelling && (
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    )}
                    {st('huy_button')}
                  </Button>
                )}
                <button
                  type="button"
                  className="btn btn-sm btn-light p-1 lh-1"
                  aria-label={st('thu_nho_label')}
                  title={st('thu_nho_label')}
                  onClick={() => onToggleCollapse(true)}
                >
                  <i className="ri-subtract-line"></i>
                </button>
                {isTerminal && (
                  <Button close type="button" aria-label={st('dong_label')} onClick={handleClose} />
                )}
              </div>
            </div>

            <div className="mt-3">
              <div className="d-flex justify-content-between small mb-1">
                <span className="fw-semibold">{job.status}</span>
                <span className="fw-semibold">{progressPercent.toFixed(2)}%</span>
              </div>
              <Progress
                value={progressPercent}
                color={
                  status === 'failed'
                    ? 'danger'
                    : status === 'stalled'
                      ? 'warning'
                      : status === 'cancelled'
                        ? 'secondary'
                        : status === 'completed'
                          ? 'success'
                          : 'primary'
                }
                style={{ height: '10px' }}
                animated={!isTerminal}
              />
            </div>

            <div className="d-flex justify-content-between text-muted small mt-2">
              <span>{st('da_xu_ly_text')}</span>
              <span className="fw-semibold">
                {formatNumber(job.processedRows)} / {formatNumber(job.totalRows)}
              </span>
            </div>

            {status === 'completed' && (
              <div className="d-flex align-items-center gap-2 mt-3">
                {downloaded ? (
                  <span className="text-success small">
                    <i className="ri-checkbox-circle-fill me-1"></i>
                    {st('tai_file_anh_qr_thanh_cong_text')}
                  </span>
                ) : downloadFailed ? (
                  <>
                    <Button color="success" size="sm" onClick={handleDownload} disabled={downloading}>
                      {downloading && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                      {st('thu_tai_lai_button')}
                    </Button>
                    <span className="text-danger small">{st('tai_file_anh_qr_that_bai_text')}</span>
                  </>
                ) : (
                  <>
                    <span className="spinner-border spinner-border-sm text-success" role="status" aria-hidden="true"></span>
                    <span className="text-success small">{st('file_anh_qr_da_san_sang_text')}</span>
                  </>
                )}
              </div>
            )}

            {status === 'cancelled' && (
              <div className="small mt-2 text-muted">{st('da_huy_theo_yeu_cau_nguoi_text')}</div>
            )}

            {(status === 'failed' || status === 'stalled') && (
              <>
                <div className={`small mt-2 text-break ${status === 'stalled' ? 'text-warning' : 'text-danger'}`}>
                  {job.errorMessage || st('xuat_file_anh_qr_that_bai_text')}
                </div>
                <div className="text-muted small mt-2 fst-italic">
                  {st('bam_xuat_file_qr_anh_tren_text')}
                </div>
                <JobErrorLogList fetchLogs={() => getQrImageJobErrorLogsApi(job.issuancePlanId)} />
              </>
            )}
          </div>
        </div>
      </Alert>

      <ReasonPromptModal
        isOpen={reasonModalOpen}
        title={st('huy_xuat_anh_qr_title')}
        label={st('ly_do_huy_xuat_anh_qr_label')}
        confirmLabel={st('huy_xuat_anh_qr_title')}
        submitting={cancelling}
        onCancel={() => setReasonModalOpen(false)}
        onSubmit={handleCancel}
      />
    </div>
  );
};

const GlobalJobProgress: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handledVerifyJobsRef = useRef<Set<string>>(new Set());
  const [exportJobs, setExportJobs] = useState<PrintPackageExportJob[]>([]);
  const [qrImageExportJobs, setQrImageExportJobs] = useState<QrImageExportJob[]>([]);
  // Mac dinh thu nho (icon tron) — nguoi dung tu bam icon "eye" o cot trang thai (PlanStatusColumn.tsx)
  // de mo rong xem chi tiet khi can, thay vi tu dong mo popup lon ngay khi job vua xuat hien.
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const { activeJobs, kyPhatHanhList } = useSelector((state: any) => state.SinhBoSoQr);

  const getPeriodName = useCallback(
    (lotteryPeriodId: number) =>
      (kyPhatHanhList || []).find((ky: any) => ky.id === lotteryPeriodId)?.periodName || null,
    [kyPhatHanhList],
  );

  const toggleExpanded = useCallback((key: string, expand: boolean) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (expand) next.add(key); else next.delete(key);
      return next;
    });
  }, []);

  // Lang nghe yeu cau thu gon/mo rong tu icon "eye" o cot trang thai (PlanStatusColumn.tsx) — 2
  // component nay o 2 nhanh cay khac nhau nen phai dong bo qua window CustomEvent.
  useEffect(() => {
    const handleToggleRequest = (event: Event) => {
      const key = (event as CustomEvent).detail?.key;
      if (!key) return;
      setExpandedKeys((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
      });
    };
    window.addEventListener(JOB_PANEL_TOGGLE_REQUEST_EVENT, handleToggleRequest);
    return () => window.removeEventListener(JOB_PANEL_TOGGLE_REQUEST_EVENT, handleToggleRequest);
  }, []);

  // Moi ky vua hoan tat can duoc "verify" tren trang Ke hoach phat hanh — dedup theo jobId bang
  // Set (thay vi 1 ref string nhu truoc) de nhieu ky hoan tat gan nhau van deu duoc xu ly dung 1 lan.
  const goToIssuancePlansForVerify = useCallback((jobId: string, lotteryPeriodId: number) => {
    if (handledVerifyJobsRef.current.has(jobId)) return;
    handledVerifyJobsRef.current.add(jobId);

    localStorage.setItem('lms_pending_verify_qr_job', JSON.stringify({
      jobId,
      lotteryPeriodId,
      completedAt: new Date().toISOString(),
    }));

    dispatch(removeActiveJob(jobId));
    navigate('/issuance-plans', { state: { refreshAt: Date.now(), verifyJobId: jobId } });
  }, [dispatch, navigate]);

  // Khoi phuc TAT CA job QR dang chay khi tai lai trang — truoc day chi khoi phuc dung job dau
  // tien tim thay trong localStorage nen ky thu 2 tro di mat theo doi tien trinh sau khi F5.
  // Ngoai ra con doi chieu voi jobs/active (cung API dung cho 2 effect khoi phuc export o duoi) de
  // tai khoan khac / tab khac / trinh duyet khac co cung quyen "genqr.active" cung thay duoc job
  // dang chay do TAI KHOAN KHAC tao (truoc day chi tai khoan da bam nut sinh moi biet jobId, vi
  // no chi duoc luu trong localStorage cua dung trinh duyet do — xem REQUIRE_FIXBUGS.md muc 9.1).
  useEffect(() => {
    let disposed = false;
    const activeKeys = Object.keys(localStorage)
      .filter((key) => key.startsWith('lms_active_job_'))
      .sort()
      .reverse();

    const restoreActiveJobs = async () => {
      const restoredIds = new Set<string>();

      for (const key of activeKeys) {
        const jobId = localStorage.getItem(key);
        const lotteryPeriodId = Number(key.replace('lms_active_job_', ''));
        if (!jobId || !lotteryPeriodId) continue;

        try {
          const response = await getJobProgressApi(jobId);
          if (disposed) return;
          const job = response?.data || response;
          const status = toJobStatus(job?.status ?? job?.Status);

          if (status === 'pending' || status === 'processing') {
            restoredIds.add(jobId);
            dispatch(upsertActiveJob({
              jobId: job.jobId,
              lotteryPeriodId: job.lotteryPeriodId ?? lotteryPeriodId,
              status,
              totalTickets: job.totalTickets ?? 0,
              processedTickets: job.processedTickets ?? 0,
              progressPercent: job.progressPercent ?? 0,
              errorMessage: job.errorMessage ?? null,
              startedAt: job.startedAt ?? null,
              completedAt: job.completedAt ?? null,
              createdAt: job.createdAt ?? new Date().toISOString(),
            }));
            continue;
          }

          localStorage.removeItem(key);
          if (status === 'completed') {
            goToIssuancePlansForVerify(job.jobId, job.lotteryPeriodId ?? lotteryPeriodId);
          }
        } catch (error: any) {
          if (error?.status === 404) {
            // Job khong con ton tai o backend (da bi xoa/purge) — xoa key ngay, neu khong se goi lai
            // API nay o MOI lan mount trang (vao 1 trang bat ky, F5...) mai mai vi khong bao gio
            // thanh cong de tu don duoc theo nhanh "completed"/"pending" o tren.
            localStorage.removeItem(key);
          }
          // Loi khac (mat mang, 5xx...): giu key, thu lai o lan mount sau — API co the tam thoi
          // khong kha dung.
        }
      }

      // Doi chieu voi jobs/active de phat hien job do TAI KHOAN KHAC dang gen ma trinh duyet nay
      // chua tung biet jobId (nen khong co trong localStorage cua no) — bat ky ai co quyen
      // "genqr.active" mo trang len deu thay duoc, dung 1 request voi 2 effect khoi phuc export o
      // duoi (fetchActiveTicketJobsOnce gom chung).
      try {
        const activeResponse = await fetchActiveTicketJobsOnce();
        if (disposed) return;
        const activePayload = activeResponse?.data || activeResponse || {};
        const activeQrJobs = activePayload.qrGenerationJobs || activePayload.QrGenerationJobs || [];

        activeQrJobs.forEach((raw: any) => {
          const jobId = raw.jobId ?? raw.JobId;
          const lotteryPeriodId = raw.lotteryPeriodId ?? raw.LotteryPeriodId;
          if (!jobId || !lotteryPeriodId || restoredIds.has(jobId)) return;

          localStorage.setItem(`lms_active_job_${lotteryPeriodId}`, jobId);
          dispatch(upsertActiveJob({
            jobId,
            lotteryPeriodId,
            status: toJobStatus(raw.status ?? raw.Status),
            totalTickets: raw.totalTickets ?? raw.TotalTickets ?? 0,
            processedTickets: raw.processedTickets ?? raw.ProcessedTickets ?? 0,
            progressPercent: raw.progressPercent ?? raw.ProgressPercent ?? 0,
            errorMessage: raw.errorMessage ?? raw.ErrorMessage ?? null,
            startedAt: raw.startedAt ?? raw.StartedAt ?? null,
            completedAt: raw.completedAt ?? raw.CompletedAt ?? null,
            createdAt: raw.createdAt ?? raw.CreatedAt ?? new Date().toISOString(),
          }));
        });
      } catch {
        // Bo qua — job do chinh tab nay tao van duoc dispatch truc tiep tu sinhBoSoQr.fulfilled.
      }
    };

    restoreActiveJobs();
    return () => {
      disposed = true;
    };
    // Chi can chay 1 lan luc mount; cac job moi phat sinh sau do da duoc dispatch truc tiep tu
    // nơi tao job (sinhBoSoQr.fulfilled), khong can effect nay chay lai.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, goToIssuancePlansForVerify]);

  const updateExportJob = useCallback((jobId: string, patch: Partial<PrintPackageExportJob>) => {
    setExportJobs((prev) => prev.map((job) => (job.jobId === jobId ? { ...job, ...patch } : job)));
  }, []);

  const addOrReplaceExportJob = useCallback((job: PrintPackageExportJob) => {
    setExportJobs((prev) => {
      const index = prev.findIndex((item) => item.jobId === job.jobId);
      if (index !== -1) {
        const next = [...prev];
        next[index] = { ...next[index], ...job };
        return next;
      }
      return [...prev, job];
    });
  }, []);

  const removeExportJob = useCallback((jobId: string) => {
    setExportJobs((prev) => prev.filter((job) => job.jobId !== jobId));
    clearStoredActivePrintExportJob(jobId);
  }, []);

  const updateQrImageExportJob = useCallback((jobId: string, patch: Partial<QrImageExportJob>) => {
    setQrImageExportJobs((prev) => prev.map((job) => (job.jobId === jobId ? { ...job, ...patch } : job)));
  }, []);

  const addOrReplaceQrImageExportJob = useCallback((job: QrImageExportJob) => {
    setQrImageExportJobs((prev) => {
      const index = prev.findIndex((item) => item.jobId === job.jobId);
      if (index !== -1) {
        const next = [...prev];
        next[index] = { ...next[index], ...job };
        return next;
      }
      return [...prev, job];
    });
  }, []);

  const removeQrImageExportJob = useCallback((jobId: string) => {
    setQrImageExportJobs((prev) => prev.filter((job) => job.jobId !== jobId));
    clearStoredActiveQrImageExportJob(jobId);
  }, []);

  // Khoi phuc TAT CA export job dang chay (truoc day chi lay phan tu [0] cua danh sach nen chi
  // theo doi duoc 1 job export tai 1 thoi diem).
  useEffect(() => {
    let disposed = false;

    const restore = async () => {
      const storedJobs = getStoredActivePrintExportJobs();
      const restoredIds = new Set<string>();

      for (const stored of storedJobs) {
        try {
          const response = await getPrintPackageExportJob(stored.jobId);
          if (disposed) return;
          const normalized = normalizeExportJob(response?.data || response);
          if (!normalized) continue;

          const status = String(normalized.status || '').toLowerCase();
          if (status === 'failed' || status === 'stalled' || status === 'cancelled') {
            clearStoredActivePrintExportJob(stored.jobId);
          }
          restoredIds.add(normalized.jobId);
          addOrReplaceExportJob(normalized);
        } catch (error: any) {
          if (error?.status === 404) {
            // Job khong con ton tai o backend — xoa key ngay, tranh goi lai vo han o moi lan mount
            // trang (cung loi voi restoreActiveJobs o effect QR ben tren).
            clearStoredActivePrintExportJob(stored.jobId);
          }
          // Loi khac: giu key, API co the tam thoi khong kha dung.
        }
      }

      try {
        const activeResponse = await fetchActiveTicketJobsOnce();
        if (disposed) return;
        const activePayload = activeResponse?.data || activeResponse || {};
        const activeExportJobs = activePayload.printPackageExportJobs || activePayload.PrintPackageExportJobs || [];
        activeExportJobs.forEach((raw: any) => {
          const normalized = normalizeExportJob(raw);
          if (!normalized || restoredIds.has(normalized.jobId)) return;
          setStoredActivePrintExportJob(normalized.jobId, normalized.issuancePlanId);
          addOrReplaceExportJob(normalized);
        });
      } catch {
        // Bo qua — panel se tu cap nhat khi co job moi qua custom event lms_print_export_job_started.
      }
    };

    restore().catch(() => undefined);

    const handleStarted = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      const normalized = normalizeExportJob(detail?.job || detail);
      if (!normalized) return;

      setStoredActivePrintExportJob(normalized.jobId, normalized.issuancePlanId);
      addOrReplaceExportJob(normalized);
    };

    window.addEventListener(PRINT_EXPORT_JOB_STARTED_EVENT, handleStarted as EventListener);
    return () => {
      disposed = true;
      window.removeEventListener(PRINT_EXPORT_JOB_STARTED_EVENT, handleStarted as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Khoi phuc TAT CA job xuat anh QR dang chay — cung pattern voi effect export job (Gui in) o tren.
  useEffect(() => {
    let disposed = false;

    const restore = async () => {
      const storedJobs = getStoredActiveQrImageExportJobs();
      const restoredIds = new Set<string>();

      for (const stored of storedJobs) {
        try {
          const response = await getQrImageExportJob(stored.jobId);
          if (disposed) return;
          const normalized = normalizeExportJob(response?.data || response);
          if (!normalized) continue;

          const status = String(normalized.status || '').toLowerCase();
          if (status === 'failed' || status === 'stalled' || status === 'cancelled') {
            clearStoredActiveQrImageExportJob(stored.jobId);
          }
          restoredIds.add(normalized.jobId);
          addOrReplaceQrImageExportJob(normalized);
        } catch (error: any) {
          if (error?.status === 404) {
            // Job khong con ton tai o backend — xoa key ngay, tranh goi lai vo han o moi lan mount
            // trang (cung loi voi restoreActiveJobs o effect QR ben tren).
            clearStoredActiveQrImageExportJob(stored.jobId);
          }
          // Loi khac: giu key, API co the tam thoi khong kha dung.
        }
      }

      try {
        const activeResponse = await fetchActiveTicketJobsOnce();
        if (disposed) return;
        const activePayload = activeResponse?.data || activeResponse || {};
        const activeQrImageJobs = activePayload.qrImageExportJobs || activePayload.QrImageExportJobs || [];
        activeQrImageJobs.forEach((raw: any) => {
          const normalized = normalizeExportJob(raw);
          if (!normalized || restoredIds.has(normalized.jobId)) return;
          setStoredActiveQrImageExportJob(normalized.jobId, normalized.issuancePlanId);
          addOrReplaceQrImageExportJob(normalized);
        });
      } catch {
        // Bo qua — panel se tu cap nhat khi co job moi qua custom event lms_qr_image_export_job_started.
      }
    };

    restore().catch(() => undefined);

    const handleStarted = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      const normalized = normalizeExportJob(detail?.job || detail);
      if (!normalized) return;

      setStoredActiveQrImageExportJob(normalized.jobId, normalized.issuancePlanId);
      addOrReplaceQrImageExportJob(normalized);
    };

    window.addEventListener(QR_IMAGE_EXPORT_JOB_STARTED_EVENT, handleStarted as EventListener);
    return () => {
      disposed = true;
      window.removeEventListener(QR_IMAGE_EXPORT_JOB_STARTED_EVENT, handleStarted as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const qrCards = (activeJobs as QrGenerationJob[]).map((job) => ({
    type: 'qr' as const,
    key: `qr:${job.jobId}`,
    job,
  }));
  const exportCards = exportJobs.map((job) => ({
    type: 'export' as const,
    key: `export:${job.jobId}`,
    job,
  }));
  const qrImageExportCards = qrImageExportJobs.map((job) => ({
    type: 'qrImageExport' as const,
    key: `qrImageExport:${job.jobId}`,
    job,
  }));
  const allCards = [...qrCards, ...exportCards, ...qrImageExportCards];
  const allCardKeys = allCards.map((card) => card.key).join('|');

  // Bao cho icon "eye" o cot trang thai (PlanStatusColumn.tsx) biet dung trang thai thu gon/mo
  // rong hien tai cua tung popup, moi khi danh sach job hoac expandedKeys thay doi.
  useEffect(() => {
    allCards.forEach((card) => {
      window.dispatchEvent(new CustomEvent(JOB_PANEL_STATE_EVENT, {
        detail: { key: card.key, collapsed: !expandedKeys.has(card.key) },
      }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCardKeys, expandedKeys]);


  // Xep chong: cac nut tron (thu gon) gom vao 1 cot gon truoc, cac popup dang mo xep ben duoi —
  // tinh truoc theo trang thai thu gon/mo rong thuc te nen khong bao gio de panel len nhau.
  const collapsedCards = allCards.filter((card) => !expandedKeys.has(card.key));
  const expandedCards = allCards.filter((card) => expandedKeys.has(card.key));

  let top = STACK_TOP_START;
  const positionedCollapsed = collapsedCards.map((card) => {
    const positioned = { ...card, top };
    top += COLLAPSED_SLOT_HEIGHT;
    return positioned;
  });
  if (collapsedCards.length > 0) top += 16;
  const positionedExpanded = expandedCards.map((card) => {
    const positioned = { ...card, top };
    top += EXPANDED_SLOT_HEIGHT;
    return positioned;
  });

  const positionedCards = [...positionedCollapsed, ...positionedExpanded];

  return (
    <>
      {positionedCards.map((card) =>
        card.type === 'qr' ? (
          <QrJobTracker
            key={card.key}
            job={card.job as QrGenerationJob}
            periodName={getPeriodName((card.job as QrGenerationJob).lotteryPeriodId)}
            topOffset={card.top}
            collapsed={!expandedKeys.has(card.key)}
            onToggleCollapse={(collapsed) => toggleExpanded(card.key, !collapsed)}
            onVerify={goToIssuancePlansForVerify}
          />
        ) : card.type === 'export' ? (
          <ExportJobCard
            key={card.key}
            job={card.job as PrintPackageExportJob}
            topOffset={card.top}
            collapsed={!expandedKeys.has(card.key)}
            onToggleCollapse={(collapsed) => toggleExpanded(card.key, !collapsed)}
            onUpdate={updateExportJob}
            onRemove={removeExportJob}
          />
        ) : (
          <QrImageExportJobCard
            key={card.key}
            job={card.job as QrImageExportJob}
            topOffset={card.top}
            collapsed={!expandedKeys.has(card.key)}
            onToggleCollapse={(collapsed) => toggleExpanded(card.key, !collapsed)}
            onUpdate={updateQrImageExportJob}
            onRemove={removeQrImageExportJob}
          />
        ),
      )}
    </>
  );
};

export default GlobalJobProgress;
